import * as React from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Box, Breadcrumbs, IconButton, Link, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ROUTE_NAMES from "routes/routeNames";
import { BRAND } from "themes";
import {
  BREADCRUMB_ROUTES,
  getParentPath,
  matchBreadcrumbRoute,
  rememberListingPath,
} from "./config";

const getHeadingLabel = (pathname: string, search: string, fallback: string) => {
  if (pathname.replace(/\/+$/, "") !== ROUTE_NAMES.MAIN_ROUTES.SERVICE_DETAIL) {
    return fallback;
  }
  return new URLSearchParams(search).get("edit") === "true"
    ? "Edit Service Type"
    : fallback;
};

export default function AppBreadcrumb() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  React.useEffect(() => {
    rememberListingPath(pathname);
  }, [pathname]);

  const match = matchBreadcrumbRoute(pathname);
  if (!match || match.hide) {
    return null;
  }

  const headingLabel = getHeadingLabel(pathname, search, match.label);
  const description = match.description || "";
  const showBackButton = Boolean(match.parent) && !match.listing;
  const parentTo = match.parent ? getParentPath(match) : "";

  const crumbs =
    match.parent && !match.listing
      ? [
          {
            label: BREADCRUMB_ROUTES[match.parent]?.label || "Back",
            to: parentTo,
          },
          { label: headingLabel },
        ]
      : [{ label: headingLabel }];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
      }}
    >
      {showBackButton && (
        <IconButton
          size="small"
          onClick={() => (parentTo ? navigate(parentTo) : navigate(-1))}
          aria-label="Go back"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "12px",
            border: `1px solid ${BRAND.border}`,
            bgcolor: "#fff",
            color: BRAND.ink,
            boxShadow: "0 8px 16px -12px rgba(27, 19, 64, 0.45)",
            cursor: "pointer",
            transition: "background-color 200ms ease, border-color 200ms ease",
            "&:hover": {
              bgcolor: BRAND.track,
              borderColor: "#DDD8EE",
            },
            "&.Mui-focusVisible": {
              outline: `2px solid ${BRAND.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      )}

      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          noWrap
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.05rem", md: "1.25rem" },
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            color: BRAND.ink,
          }}
        >
          {headingLabel}
        </Typography>

        {description ? (
          <Typography
            noWrap
            sx={{
              display: { xs: "none", sm: "block" },
              fontSize: "0.8rem",
              fontWeight: 500,
              color: "#5C5870",
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        ) : (
          crumbs.length > 1 && (
            <Breadcrumbs
              aria-label="breadcrumb"
              separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
              sx={{
                display: { xs: "none", sm: "flex" },
                mt: 0.15,
                maxWidth: "100%",
                "& .MuiBreadcrumbs-ol": {
                  alignItems: "center",
                  flexWrap: "nowrap",
                },
                "& .MuiBreadcrumbs-li": {
                  overflow: "hidden",
                },
                "& .MuiBreadcrumbs-separator": {
                  mx: 0.35,
                  color: "text.disabled",
                },
              }}
            >
              {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                if (isLast || !crumb.to) {
                  return (
                    <Typography
                      key={crumb.label}
                      noWrap
                      sx={{
                        color: BRAND.primary,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      }}
                    >
                      {crumb.label}
                    </Typography>
                  );
                }
                return (
                  <Link
                    key={crumb.label}
                    component={RouterLink}
                    to={crumb.to}
                    underline="hover"
                    sx={{
                      color: "#5C5870",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {crumb.label}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )
        )}
      </Box>
    </Box>
  );
}
